import { EditorStarterItem } from '../items/item-type';
import { CroppableItem, getCropFromItem } from './get-crop-from-item';
import { getRectAfterCrop } from './get-dimensions-after-crop';

/**
 * 크롭 모드에서 "원본 영상 크기"를 조정하는 순수 변환.
 *
 * 크롭 프레임(화면에 보이는 잘린 영역, rectAfterCrop)은 캔버스상 위치·크기 그대로 고정한 채,
 * 그 안의 원본 미디어(item.width/height)만 scaleFactor 배로 키우거나 줄인다. 결과적으로
 * 고정된 프레임 안에서 영상이 확대/축소되어 보인다. 확대는 프레임 중심을 기준으로 한다.
 *
 * item.width/height 는 캔버스 픽셀 좌표이고, 보이는 창의 폭 = width*(1-cropLeft-cropRight)
 * 이므로, 미디어를 s 배 키우면 크롭 분수를 늘려 창의 캔버스 크기를 그대로 유지한다.
 */

/** 창이 미디어를 벗어나지 않도록 하는 하한(창이 미디어를 꽉 채우는 배율)과 과확대 상한. */
const MIN_VISIBLE_FRACTION = 0.05; // 창이 미디어의 5% 미만이 되지 않도록(최대 ~20x)

const clampUnit = (value: number, maxStart: number): number =>
  Math.max(0, Math.min(maxStart, value));

/**
 * 현재 원본 확대 배율. 1 = 미디어가 창을 딱 채움(확대 없음), 2 = 창이 미디어의 절반(2배 확대).
 * 가로축 기준(영상은 비율 고정이라 세로도 동일).
 */
export const getCropSourceZoom = (item: EditorStarterItem): number => {
  const crop = getCropFromItem(item);
  if (!crop) return 1;
  const visibleFractionW = 1 - crop.cropLeft - crop.cropRight;
  if (visibleFractionW <= 0) return 1;
  return 1 / visibleFractionW;
};

/**
 * 크롭 프레임을 고정한 채 원본 미디어를 scaleFactor 배로 스케일한 새 item 을 돌려준다.
 * scaleFactor > 1 이면 확대(미디어가 커지고 창은 그대로 → 영상이 더 크게 보임), < 1 이면 축소.
 */
export const scaleCropSourceAroundWindowCenter = (
  item: CroppableItem,
  scaleFactor: number,
): CroppableItem => {
  const crop = getCropFromItem(item);
  if (!crop || !Number.isFinite(scaleFactor) || scaleFactor <= 0) {
    return item;
  }

  const width = item.width;
  const height = item.height;
  if (width <= 0 || height <= 0) {
    return item;
  }
  const visibleFractionW = 1 - crop.cropLeft - crop.cropRight;
  const visibleFractionH = 1 - crop.cropTop - crop.cropBottom;
  if (visibleFractionW <= 0 || visibleFractionH <= 0) {
    return item;
  }

  // 고정할 크롭 프레임(캔버스 좌표).
  const windowRect = getRectAfterCrop(item);

  // 프레임 중심이 미디어에서 차지하는 위치(분수). 확대의 기준점.
  const centerFractionX = crop.cropLeft + visibleFractionW / 2;
  const centerFractionY = crop.cropTop + visibleFractionH / 2;

  // 배율 clamp: 창이 미디어를 벗어나지 않도록(하한) + 과확대 방지(상한).
  const minScale = Math.max(visibleFractionW, visibleFractionH); // 창이 미디어를 꽉 채우는 지점
  const maxScale = Math.min(
    visibleFractionW / MIN_VISIBLE_FRACTION,
    visibleFractionH / MIN_VISIBLE_FRACTION,
  );
  const scale = Math.max(minScale, Math.min(maxScale, scaleFactor));

  const newWidth = width * scale;
  const newHeight = height * scale;
  // 창의 캔버스 크기를 유지하려면 보이는 분수를 1/scale 로 줄인다.
  const newVisibleFractionW = windowRect.width / newWidth;
  const newVisibleFractionH = windowRect.height / newHeight;

  // 프레임 중심을 유지하되, 창이 미디어를 벗어나면 pan 을 clamp.
  const newCropLeft = clampUnit(
    centerFractionX - newVisibleFractionW / 2,
    1 - newVisibleFractionW,
  );
  const newCropTop = clampUnit(
    centerFractionY - newVisibleFractionH / 2,
    1 - newVisibleFractionH,
  );
  const newCropRight = 1 - newVisibleFractionW - newCropLeft;
  const newCropBottom = 1 - newVisibleFractionH - newCropTop;

  // 창의 캔버스 위치(left/top)를 그대로 유지하도록 미디어 원점을 역산.
  const newLeft = windowRect.left - newCropLeft * newWidth;
  const newTop = windowRect.top - newCropTop * newHeight;

  return {
    ...item,
    width: newWidth,
    height: newHeight,
    left: newLeft,
    top: newTop,
    cropLeft: newCropLeft,
    cropTop: newCropTop,
    cropRight: newCropRight,
    cropBottom: newCropBottom,
  };
};
